import { useState } from 'react'
import { useParams } from 'react-router-dom'

import {
  useBrandSettingsApi,
  useConnectInstagramApi,
  useInstagramStatusApi,
  useUpdateBrandSettingsApi
} from '@/common/api/modules/ugc'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

/**
 * Instagram設定。
 *
 * Instagram に関する設定をこの1画面に集約する。以前は「SNS連携」（ブランド情報＋
 * ハッシュタグ）と「API連携設定」（接続手順）に分かれていて、**どちらの画面でも
 * 実際には接続できなかった**——接続を保存する API は
 * `POST /ugc/collection/instagram/connect` だけなのに、それを呼ぶ UI が
 * どこにも無かった。ここでその1本に繋ぐ。
 *
 * 接続とそれ以外で保存の押し方が違う点に注意：
 * - **接続**はトークンの検証を伴うため、その場で即時に実行する（サーバ側が
 *   Graph API へ接続テストしてから保存する）。
 * - **ハッシュタグ・同期頻度・ブランド情報**はまとめて「保存」で更新する。
 */

const input =
  'shadow-inset text-size-13 w-full rounded-lg border border-border-input bg-input px-3 py-2'

const SYNC_FREQUENCIES = [
  { key: 'daily', label: '毎日' },
  { key: 'weekly', label: '毎週' },
  { key: 'manual', label: '手動のみ（自動収集しない）' }
] as const

/** ハッシュタグは先頭の # を落として保存する（API へ渡すのはタグ名だけ） */
const normalizeHashtag = (raw: string) => raw.trim().replace(/^#+/, '')

const UGCInstagramSettingsPage = () => {
  const { siteId: siteIdParam } = useParams()
  const siteId = Number(siteIdParam)

  // 編集中の値だけを持ち、未編集の項目は API の値をそのまま表示する。
  // API 応答を state に写し込むと、保存後の再取得と入力中の値が競合する
  const [edit, setEdit] = useState<{
    brand_name?: string
    brand_logo_url?: string
    default_consent_message?: string
    review_collection_enabled?: boolean
    ig_watch_hashtags?: string[]
    ig_sync_frequency?: string
  }>({})
  const [newHashtag, setNewHashtag] = useState('')
  const [connectForm, setConnectForm] = useState({ access_token: '', business_account_id: '' })
  const [savedMessage, setSavedMessage] = useState<string | null>(null)
  const [connectError, setConnectError] = useState<string | null>(null)

  const { data: brand, isLoading, mutate: reloadBrand } = useBrandSettingsApi({ site_id: siteId })
  const { data: status, mutate: reloadStatus } = useInstagramStatusApi({ site_id: siteId })

  const { trigger: save, isMutating: saving } = useUpdateBrandSettingsApi({
    onSuccess: () => {
      setEdit({})
      setSavedMessage('保存しました')
      reloadBrand()
    }
  })

  const { trigger: connect, isMutating: connecting } = useConnectInstagramApi({
    // 接続の失敗理由（トークン期限切れ・権限不足など）は画面に出す。
    // トーストだけだと、入力し直す場所から離れた位置で消えてしまう
    errorNotShown: true,
    onSuccess: () => {
      setConnectForm({ access_token: '', business_account_id: '' })
      setConnectError(null)
      reloadStatus()
      reloadBrand()
    },
    onError: async (res) => {
      const detail = await res
        .json()
        .then((d) => d?.detail)
        .catch(() => null)
      setConnectError(detail || '接続できませんでした。トークンとアカウントIDをご確認ください。')
    }
  })

  const hashtags = edit.ig_watch_hashtags ?? brand?.ig_watch_hashtags ?? []
  const syncFrequency = edit.ig_sync_frequency ?? brand?.ig_sync_frequency ?? 'daily'
  const brandName = edit.brand_name ?? brand?.brand_name ?? ''
  const logoUrl = edit.brand_logo_url ?? brand?.brand_logo_url ?? ''
  const consentMessage = edit.default_consent_message ?? brand?.default_consent_message ?? ''

  const connected = !!status?.connected
  const account = (status?.account ?? {}) as { username?: string; followers_count?: number }

  const onAddHashtag = () => {
    const tag = normalizeHashtag(newHashtag)
    // 同じタグを2回登録しても収集対象は増えない。追加時点で弾く
    if (!tag || hashtags.includes(tag)) {
      setNewHashtag('')
      return
    }
    setEdit({ ...edit, ig_watch_hashtags: [...hashtags, tag] })
    setNewHashtag('')
  }

  const onSave = () => {
    setSavedMessage(null)
    save({
      site_id: siteId,
      brand_name: brandName,
      brand_logo_url: logoUrl,
      default_consent_message: consentMessage,
      ig_watch_hashtags: hashtags,
      ig_sync_frequency: syncFrequency
    })
  }

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='Instagram設定'
        description='Instagram アカウントの接続と、収集する投稿の条件、UGC で使うブランド情報を設定します。'
        rightNode={
          <Button color={ButtonColor.PRIMARY} loading={saving} disabled={saving} onClick={onSave}>
            保存
          </Button>
        }
      />

      <Content>
        {savedMessage && (
          <Text size={13} color={TextColor.SUCCESS}>
            {savedMessage}
          </Text>
        )}

        <Area>
          <Heading subText='Instagram Business Account のアクセストークンで接続します。接続すると、タグ付き投稿とハッシュタグ投稿を収集できるようになります。'>
            アカウント接続
          </Heading>
          <AreaContent>
            <div className='flex flex-col gap-4'>
              <div className='flex flex-wrap items-center gap-3'>
                <span
                  className={`inline-block h-2.5 w-2.5 rounded-full ${
                    connected ? 'bg-fill-success' : 'bg-fill-critical'
                  }`}
                />
                <Text size={13} weight='bold' color={connected ? TextColor.SUCCESS : TextColor.CRITICAL}>
                  {connected ? '接続済み' : '未接続'}
                </Text>
                {account.username && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    @{account.username}
                  </Text>
                )}
                {account.followers_count != null && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    フォロワー {account.followers_count.toLocaleString()}
                  </Text>
                )}
                {brand?.ig_business_account_id && (
                  <Text size={12} color={TextColor.SECONDARY}>
                    Account ID: {brand.ig_business_account_id}
                  </Text>
                )}
              </div>

              {/* トークンが切れている場合、保存はされているので「未接続」だけでは原因が分からない */}
              {status?.error && (
                <Text size={12} color={TextColor.CRITICAL}>
                  {status.error}（再接続してください）
                </Text>
              )}

              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <label className='flex flex-col gap-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    アクセストークン
                  </Text>
                  <input
                    className={input}
                    type='password'
                    autoComplete='off'
                    value={connectForm.access_token}
                    placeholder={connected ? '再接続する場合のみ入力' : 'IGQVJ... で始まる長期トークン'}
                    onChange={(e) =>
                      setConnectForm({ ...connectForm, access_token: e.target.value })
                    }
                  />
                </label>
                <label className='flex flex-col gap-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    Instagram Business Account ID
                  </Text>
                  <input
                    className={input}
                    value={connectForm.business_account_id}
                    placeholder='17841400000000000'
                    onChange={(e) =>
                      setConnectForm({ ...connectForm, business_account_id: e.target.value })
                    }
                  />
                </label>
              </div>

              {connectError && (
                <Text size={12} color={TextColor.CRITICAL}>
                  {connectError}
                </Text>
              )}

              <div className='flex flex-wrap items-center gap-3'>
                <Button
                  color={ButtonColor.SECONDARY}
                  loading={connecting}
                  disabled={
                    connecting || !connectForm.access_token || !connectForm.business_account_id
                  }
                  onClick={() => {
                    setConnectError(null)
                    connect({
                      site_id: siteId,
                      access_token: connectForm.access_token.trim(),
                      business_account_id: connectForm.business_account_id.trim()
                    })
                  }}
                >
                  {connected ? '再接続する' : '接続する'}
                </Button>
                <Text size={12} color={TextColor.SECONDARY}>
                  接続時に Instagram へ問い合わせて確認します。保存されるトークンは暗号化されます。
                </Text>
              </div>

              <details className='border-secondary rounded-lg border p-3'>
                <summary className='text-size-13 cursor-pointer'>
                  トークンの取得手順（Meta の管理画面での操作）
                </summary>
                <ol className='text-size-12 text-secondary mt-2 list-decimal pl-5'>
                  <li>Instagram アカウントをビジネスアカウントに切り替える</li>
                  <li>Facebook ページと連携する（Instagram 設定 → リンク済みアカウント）</li>
                  <li>
                    <a
                      className='text-link'
                      href='https://business.facebook.com/'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Meta Business Suite
                    </a>
                    にログインする
                  </li>
                  <li>
                    <a
                      className='text-link'
                      href='https://developers.facebook.com/tools/explorer/'
                      target='_blank'
                      rel='noopener noreferrer'
                    >
                      Graph API Explorer
                    </a>
                    でトークンを発行する（権限: instagram_basic, pages_show_list）
                  </li>
                  <li>
                    Account ID は <code>me/accounts?fields=instagram_business_account</code> で確認する
                  </li>
                  <li>上のフォームに貼り付けて「接続する」を押す</li>
                </ol>
              </details>
            </div>
          </AreaContent>
        </Area>

        <Area>
          <Heading subText='どの投稿を、どのくらいの頻度で自動収集するかを決めます。監視ハッシュタグの投稿に加え、自社アカウントがタグ付けされた投稿も収集します。自動収集を止めたいときは頻度を「手動のみ」にしてください（Instagram UGC の画面から手動で同期できます）。'>
            収集する投稿
          </Heading>
          <AreaContent>
            <div className='flex flex-col gap-4'>
              <div className='flex flex-col gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  監視ハッシュタグ
                </Text>
                <div className='flex flex-wrap items-center gap-2'>
                  {hashtags.length === 0 && (
                    <Text size={12} color={TextColor.SECONDARY}>
                      未登録です
                    </Text>
                  )}
                  {hashtags.map((tag) => (
                    <span
                      key={tag}
                      className='bg-surface-secondary text-size-12 inline-flex items-center gap-1 rounded-lg px-2 py-1'
                    >
                      #{tag}
                      <button
                        type='button'
                        className='text-critical cursor-pointer'
                        aria-label={`#${tag} を削除`}
                        onClick={() =>
                          setEdit({
                            ...edit,
                            ig_watch_hashtags: hashtags.filter((t) => t !== tag)
                          })
                        }
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <div className='flex flex-wrap items-center gap-2'>
                  <input
                    className={`${input} md:max-w-xs`}
                    value={newHashtag}
                    placeholder='例）ブランド名'
                    onChange={(e) => setNewHashtag(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        // フォーム送信で画面が再読み込みされるのを防ぐ
                        e.preventDefault()
                        onAddHashtag()
                      }
                    }}
                  />
                  <Button color={ButtonColor.SECONDARY} onClick={onAddHashtag}>
                    追加
                  </Button>
                </div>
              </div>

              <div className='flex flex-col gap-2'>
                <Text size={12} color={TextColor.SECONDARY}>
                  自動収集の頻度（ON/OFF もここで切り替えます）
                </Text>
                <div className='flex flex-wrap gap-2'>
                  {SYNC_FREQUENCIES.map((f) => (
                    <Button
                      key={f.key}
                      color={syncFrequency === f.key ? ButtonColor.PRIMARY : ButtonColor.SECONDARY}
                      onClick={() => setEdit({ ...edit, ig_sync_frequency: f.key })}
                    >
                      {f.label}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          </AreaContent>
        </Area>

        <Area>
          {/* 2026-09-29 修正：説明が実際の使い道と合っておらず、何に使われるのか分からなかった */}
          <Heading subText='お客様の目に触れるメールやフォームに表示される情報です（サイトのUGC表示枠には使いません）。項目ごとの表示先は各欄の下に書いています。'>
            ブランド情報
          </Heading>
          <AreaContent>
            <div className='flex flex-col gap-4'>
              <div className='grid grid-cols-1 gap-3 md:grid-cols-2'>
                <label className='flex flex-col gap-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    ブランド名
                  </Text>
                  <input
                    className={input}
                    value={brandName}
                    placeholder='ブランド名'
                    onChange={(e) => setEdit({ ...edit, brand_name: e.target.value })}
                  />
                  <Text size={12} color={TextColor.TERTIARY}>
                    表示先：レビュー依頼メールの件名とヘッダ、レビュー投稿フォームの見出し、許諾フォームの見出し
                  </Text>
                </label>
                <label className='flex flex-col gap-1'>
                  <Text size={12} color={TextColor.SECONDARY}>
                    ロゴURL
                  </Text>
                  <input
                    className={input}
                    value={logoUrl}
                    placeholder='https://...'
                    onChange={(e) => setEdit({ ...edit, brand_logo_url: e.target.value })}
                  />
                  <Text size={12} color={TextColor.TERTIARY}>
                    表示先：レビュー依頼メールのヘッダ画像、レビュー投稿フォーム（未設定ならブランド名の文字で表示）
                  </Text>
                </label>
              </div>

              <label className='flex flex-col gap-1'>
                <Text size={12} color={TextColor.SECONDARY}>
                  デフォルト許諾メッセージ
                </Text>
                <textarea
                  className={`${input} h-24`}
                  value={consentMessage}
                  placeholder='あなたの投稿をブランドサイトで紹介させていただきたいのですが...'
                  onChange={(e) => setEdit({ ...edit, default_consent_message: e.target.value })}
                />
                <Text size={12} color={TextColor.TERTIARY}>
                  表示先：Instagram の投稿者に「サイトで紹介してよいか」を確認する許諾フォームの本文（未設定なら標準の文面）
                </Text>
              </label>

            </div>
          </AreaContent>
        </Area>

        {isLoading && (
          <Text size={13} color={TextColor.SECONDARY}>
            読み込み中...
          </Text>
        )}
      </Content>
    </Main>
  )
}

export default UGCInstagramSettingsPage
